import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import PublicTermsApply from "@/pages/PublicTermsApply";
import TermsApplications from "@/pages/dashboard/TermsApplications";
import CommercialAccounts, { TermsRedirect } from "@/pages/dashboard/CommercialAccounts";
import Demo from "@/pages/Demo";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Invite from "@/pages/Invite";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import AgentRoster from "@/pages/dashboard/AgentRoster";
import KpiTracker from "@/pages/dashboard/KpiTracker";
import L10 from "@/pages/dashboard/L10";
import Cancellations from "@/pages/dashboard/Cancellations";
import DispatchAssist from "@/pages/dashboard/DispatchAssist";
import TechProfilesRead from "@/pages/dashboard/TechProfilesRead";
import Comp from "@/pages/dashboard/Comp";
import TechPayStatements from "@/pages/dashboard/TechPayStatements";
import Payroll from "@/pages/dashboard/Payroll";
import PayrollRunDetail from "@/pages/dashboard/PayrollRunDetail";
import PayrollApproval from "@/pages/dashboard/PayrollApproval";
import CsrBackpayReport from "@/pages/dashboard/CsrBackpayReport";
import CsrSalesLog from "@/pages/dashboard/CsrSalesLog";
import Settings from "@/pages/dashboard/Settings";
import Notifications from "@/pages/dashboard/Notifications";
import ThankYouCards from "@/pages/dashboard/ThankYouCards";
import HiringSettings from "@/pages/dashboard/HiringSettings";
import HiringPipeline from "@/pages/dashboard/HiringPipeline";
import HiringCalendar from "@/pages/dashboard/HiringCalendar";
import HiringPending from "@/pages/dashboard/HiringPending";
import HiringCandidateDetail from "@/pages/dashboard/HiringCandidateDetail";
import HiringIntake from "@/pages/public/HiringIntake";
import PublicApplyLanding, { PublicApplyThanks } from "@/pages/public/PublicApplyLanding";
import TeamReviewMagicLink from "@/pages/public/TeamReviewMagicLink";
import Collections from "@/pages/dashboard/Collections";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminRecoverySequences from "@/pages/admin/AdminRecoverySequences";
import AdminTechProfiles from "@/pages/admin/AdminTechProfiles";
import AdminCsrProfiles from "@/pages/admin/AdminCsrProfiles";
import AdminPlatformAlerts from "@/pages/admin/AdminPlatformAlerts";
import AdminIngestWarns from "@/pages/admin/AdminIngestWarns";
import AdminTechGoalsAudit from "@/pages/admin/AdminTechGoalsAudit";
import AdminCancelAttributionAudit from "@/pages/admin/AdminCancelAttributionAudit";
import AdminPayrollStaleDrafts from "@/pages/admin/AdminPayrollStaleDrafts";

// Redirect helper for post-login "figure out where to send this user"
function TenantIndex() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}/app/overview`} replace />;
}

export default function App() {
  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public candidate intake — no auth. Phase B (2026-08-21 pt.16). */}
            <Route path="/hiring/intake/:token" element={<HiringIntake />} />
            {/* Public tokenless "Apply Now" — Phase H (2026-08-26 pt.39). */}
            <Route path="/apply/:tenantSlug/:postingSlug" element={<PublicApplyLanding />} />
            <Route path="/apply/:tenantSlug/:postingSlug/thanks" element={<PublicApplyThanks />} />
            {/* Team Review magic link — public, no auth. pt.87 (2026-09-18). */}
            <Route path="/team-review/:token" element={<TeamReviewMagicLink />} />
            <Route path="/terms/apply/:tenantSlug" element={<PublicTermsApply />} />

            {/* Platform admin (no slug) */}
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminTenants />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminLeads />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminAuditLog />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payroll-stale-drafts"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminPayrollStaleDrafts />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/recovery"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminRecoverySequences />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tech-profiles"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "owner"]}>
                  <DashboardLayout>
                    <AdminTechProfiles />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/csr-staff"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "owner"]}>
                  <DashboardLayout>
                    <AdminCsrProfiles />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/platform-alerts"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminPlatformAlerts />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ingest-warns"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminIngestWarns />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tech-goals-audit"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminTechGoalsAudit />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cancel-attribution-audit"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout>
                    <AdminCancelAttributionAudit />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Tenant-scoped */}
            <Route path="/:slug/login" element={<Login />} />
            <Route path="/:slug/invite/:token" element={<Invite />} />
            <Route path="/:slug" element={<TenantIndex />} />
            <Route
              path="/:slug/app"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<Overview />} />
              <Route path="agents" element={<AgentRoster />} />
              <Route path="kpi" element={<KpiTracker />} />
              <Route path="l10" element={<L10 />} />
              <Route path="cancellations" element={<Cancellations />} />
              <Route path="dispatch-assist" element={<DispatchAssist />} />
              <Route path="tech-profiles" element={<TechProfilesRead />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="payroll/csr-backpay" element={<CsrBackpayReport />} />
              <Route path="payroll/csr-sales" element={
                <ProtectedRoute allowedRoles={["platform_admin", "owner", "administrative", "call_center"]}>
                  <CsrSalesLog />
                </ProtectedRoute>
              } />
              <Route path="payroll/runs/:runId" element={<PayrollRunDetail />} />
              <Route path="payroll/test-runs/:runId" element={<PayrollRunDetail />} />
              <Route path="payroll-approval/:runId" element={<PayrollApproval />} />
              <Route path="comp" element={<Comp />} />
              <Route path="comp/tech-pay" element={<TechPayStatements />} />
              <Route path="settings" element={<Settings />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="thank-you-cards" element={<ThankYouCards />} />
              <Route path="hiring/settings" element={<HiringSettings />} />
              <Route path="hiring/pipeline" element={<HiringPipeline />} />
              <Route path="hiring/calendar" element={<HiringCalendar />} />
              <Route path="hiring/pending" element={<HiringPending />} />
              <Route path="commercial-accounts" element={<CommercialAccounts />} />
              <Route path="commercial-accounts/:tab" element={<CommercialAccounts />} />
              <Route path="commercial-accounts/new/:appId" element={<TermsApplications />} />
              <Route path="terms" element={<TermsRedirect />} />
              <Route path="terms/register" element={<TermsRedirect />} />
              <Route path="terms/legacy" element={<TermsRedirect />} />
              <Route path="terms/:appId" element={<TermsRedirect />} />
              <Route path="hiring/candidates" element={<HiringPipeline />} />
              <Route path="hiring/candidates/:id" element={<HiringCandidateDetail />} />
              <Route path="collections" element={<Collections />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
